import { useSiteMonitoringContext } from '@components/StoreMonitoring/SiteMonitoringContext'
import { BBDTag, GraviButton, Horizontal, Texto, useDeviceType, Vertical } from '@gravitate-js/excalibrr'
import { Checkbox } from 'antd'
import { uniqueId } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import { AnalyticIcon } from './TankAlertDisplay/components/AnalyticIcon'

export type AlertObject = {
  title: string
  main_metric: number
  threshold?: number
  severity?: string
}

type AlertRowProps = {
  alert: AlertObject
}

type MobileAlertRowProps = {
  alert: AlertObject
  filters: { alerts: AlertObject['title'][] }
  setFilters: (filters: { alerts: AlertObject['title'][] }) => void
}

type AlertFilterCardsProps = {
  data: AlertObject[]
  hideHeader?: boolean
  onClose?: () => void
}

export function AlertFilterCards({ data, hideHeader, onClose }: AlertFilterCardsProps) {
  const { isMobile, isLandscape } = useDeviceType()
  const { filters, filterOptions, setFilters } = useSiteMonitoringContext()

  const orderedAlerts = useMemo(() => {
    if (!filterOptions?.alerts || !data) return []
    const orderMap = new Map<string, number>(filterOptions.alerts.map((title, index) => [title, index]))
    const alertData = data
      .filter((value) => !value.title.includes('Results'))
      .sort((a, b) => {
        const indexA = orderMap.get(a.title) ?? Infinity
        const indexB = orderMap.get(b.title) ?? Infinity
        return indexA - indexB
      })

    return alertData
  }, [data, filterOptions?.alerts])

  const [mobileFilters, setMobileFilters] = useState(filters)
  const mobileAlertOptions = useMemo(() => {
    return filterOptions?.alerts
      .map((title) => {
        const d = data?.find((item) => item.title === title)
        return { title, main_metric: d?.main_metric ?? 0, severity: d?.severity ?? 'warning' }
      })
      .sort((a, b) => b.main_metric - a.main_metric)
  }, [data, filterOptions?.alerts])

  useEffect(() => {
    if (isMobile) setMobileFilters(filters)
  }, [filters])

  const numAlerts = useMemo(
    () => data?.filter((item) => !item.title.includes('Results')).reduce((acc, val) => acc + val.main_metric, 0),
    [data]
  )

  const alertFilterOptions = isMobile ? mobileAlertOptions : orderedAlerts

  if (!data || data.length === 0) return null

  return (
    <Vertical>
      {!hideHeader && (
        <Horizontal
          verticalCenter
          gap={8}
          bg='elevated'
          style={{ alignItems: 'flex-end', padding: '18px 24px' }}
          className='border-bottom border-left border-right border-top'
        >
          <Texto category='h4'>Tank Alerts</Texto>
          <Texto category='p1' style={{ paddingBottom: '0.1em' }}>
            {numAlerts} Results
          </Texto>
        </Horizontal>
      )}
      <Vertical
        style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: isMobile ? `calc(80dvh - ${isLandscape ? '68px' : '0px'})` : 'inherit',
        }}
      >
        {alertFilterOptions.map((value) => {
          const id = uniqueId()
          return isMobile ? (
            <MobileAlertRow key={id} alert={value} filters={mobileFilters} setFilters={setMobileFilters} />
          ) : (
            <AlertRow key={id} alert={value} />
          )
        })}
      </Vertical>
      {isMobile && (
        <Horizontal className='p-4 mb-1' horizontalCenter gap='12px' style={{ backgroundColor: 'var(--bg-1)' }}>
          <GraviButton className='flex-1' onClick={onClose}>
            Cancel
          </GraviButton>
          <GraviButton
            color='primary'
            className='flex-1'
            onClick={() => {
              setFilters(mobileFilters)
              onClose()
            }}
          >
            Apply Filter
          </GraviButton>
        </Horizontal>
      )}
    </Vertical>
  )
}

function AlertRow({ alert }: AlertRowProps) {
  let isEmpty = false
  if (alert.title.toLowerCase() === 'empty') {
    isEmpty = true
  }
  let iconTitle = alert.title
  let alertTitle = alert.title
  const isRunoutAlert = alertTitle.toLowerCase().includes('runout within')
  if (isRunoutAlert) {
    alertTitle = alert.threshold ? `Runout within ${alert.threshold} hours` : alertTitle
    iconTitle = `runout within ${alert.severity}`
  }

  return (
    <div className='p-3 flex items-center border-bottom' style={{ backgroundColor: 'var(--bg-1)' }}>
      <div className='flex justify-center' style={{ width: 50 }}>
        <AnalyticIcon title={iconTitle} threshold={alert?.threshold} />
      </div>
      <Texto category='h6' style={{ color: isEmpty ? 'var(--theme-error' : '' }}>
        {isEmpty ? `TANK ${alertTitle.toUpperCase()}` : alertTitle}
      </Texto>
      <Texto className='pl-1' category='h6' style={{ color: isEmpty ? 'var(--theme-error)' : '' }}>
        ({alert.main_metric})
      </Texto>
    </div>
  )
}

function MobileAlertRow({ alert, filters, setFilters }: MobileAlertRowProps) {
  const isEmpty = alert.title.toLowerCase() === 'empty'
  const isRunoutAlert = alert.title.toLowerCase().includes('runout within')
  const iconTitle = isRunoutAlert ? `runout within ${alert.severity}` : alert.title

  function onAlertOptionClick({ target }) {
    const alertFilters = filters?.alerts ?? []
    const isChecked = target.checked
    setFilters({
      ...filters,
      alerts: isChecked ? [...alertFilters, alert.title] : alertFilters.filter((item) => item !== alert.title),
    })
  }

  return (
    <div className='p-3 flex items-center border-bottom' style={{ backgroundColor: 'var(--bg-1)' }}>
      <Checkbox checked={filters?.alerts?.includes(alert.title)} onClick={onAlertOptionClick} />
      <div className='flex justify-center' style={{ width: 50 }}>
        <AnalyticIcon title={iconTitle} threshold={alert?.threshold} />
      </div>
      <Texto style={{ color: isEmpty ? 'var(--theme-error' : '' }}>
        {isEmpty ? `TANK ${alert.title.toUpperCase()}` : alert.title}
      </Texto>
      <BBDTag style={{ marginLeft: 'auto' }}>{alert.main_metric}</BBDTag>
    </div>
  )
}
